import { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, Plus, Save, Trash2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api/v1';

interface HomepageBlock {
  id?: string;
  block_type: string;
  sort_order: number;
  config: string;
  is_active: boolean;
}

export default function HomepageBuilder() {
  const [blocks, setBlocks] = useState<HomepageBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // New Block Form State
  const [newBlockType, setNewBlockType] = useState('hero_banner');
  const [newBlockConfig, setNewBlockConfig] = useState('{}');

  const fetchBlocks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwt_token');
      const response = await fetch(`${API_URL}/homepage`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Assuming data is an array of blocks
        const sorted = (Array.isArray(data) ? data : data.data || []).sort((a: any, b: any) => a.sort_order - b.sort_order);
        setBlocks(sorted.map((b: any) => ({ ...b, config: typeof b.config === 'object' ? JSON.stringify(b.config, null, 2) : b.config })));
      }
    } catch (error) {
      console.error('Failed to fetch homepage blocks', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlocks();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('jwt_token');
      
      // Convert config strings back to objects and reset sort_order
      const payload = blocks.map((b, index) => ({
        ...b,
        sort_order: index, 
        config: b.config ? JSON.parse(b.config) : {}
      }));

      const response = await fetch(`${API_URL}/homepage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ blocks: payload })
      });

      if (response.ok) {
        alert('Layout saved successfully!');
      } else {
        alert('Failed to save layout.');
      }
    } catch (error) {
      console.error('Save failed', error);
      alert('Error parsing JSON config or saving.');
    } finally {
      setSaving(false);
    }
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    const temp = newBlocks[index];
    newBlocks[index] = newBlocks[index - 1];
    newBlocks[index - 1] = temp;
    setBlocks(newBlocks);
  };

  const moveDown = (index: number) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    const temp = newBlocks[index];
    newBlocks[index] = newBlocks[index + 1];
    newBlocks[index + 1] = temp;
    setBlocks(newBlocks);
  };

  const removeBlock = (index: number) => {
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    setBlocks(newBlocks);
  };

  const addBlock = () => {
    try {
      // Test if JSON is valid before adding
      JSON.parse(newBlockConfig);
      const newBlock: HomepageBlock = {
        block_type: newBlockType,
        sort_order: blocks.length,
        config: newBlockConfig,
        is_active: true,
      };
      setBlocks([...blocks, newBlock]);
      setNewBlockType('hero_banner');
      setNewBlockConfig('{}');
    } catch (e) {
      alert('Invalid JSON in block configuration.');
    }
  };

  const updateConfig = (index: number, val: string) => {
    const newBlocks = [...blocks];
    newBlocks[index].config = val;
    setBlocks(newBlocks);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Homepage Builder</h1>
          <p className="text-slate-600">Manage the dynamic blocks on the public homepage.</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 disabled:opacity-50 transition-colors"
        >
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Layout'}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 animate-pulse">Loading blocks...</div>
      ) : (
        <div className="space-y-4 mb-8">
          {blocks.map((block, index) => (
            <div key={block.id || index} className="flex gap-4 items-start bg-white p-4 border border-slate-200 rounded-lg shadow-sm">
              <div className="flex flex-col gap-1 pt-2">
                <button
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-1 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ArrowUp size={20} />
                </button>
                <button
                  onClick={() => moveDown(index)}
                  disabled={index === blocks.length - 1}
                  className="p-1 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ArrowDown size={20} />
                </button>
              </div>
              
              <div className="flex-1">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                    <h3 className="font-medium text-slate-800 capitalize">
                      {block.block_type.replace('_', ' ')}
                    </h3>
                  </div>
                  <button 
                    onClick={() => removeBlock(index)}
                    className="text-slate-400 hover:text-red-600 transition-colors"
                    title="Remove Block"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">JSON Configuration</label>
                  <textarea
                    value={block.config}
                    onChange={(e) => updateConfig(index, e.target.value)}
                    className="w-full h-24 p-3 text-sm font-mono border border-slate-200 rounded-md focus:border-slate-400 focus:ring-1 focus:ring-slate-400 outline-none bg-slate-50 transition-all"
                  />
                </div>
              </div>
            </div>
          ))}
          
          {blocks.length === 0 && (
            <div className="text-center p-12 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 bg-slate-50">
              No blocks active. Add a new block below to get started.
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
          <Plus size={20} className="text-slate-400" />
          Add New Block
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="col-span-1">
            <label className="block text-sm font-medium text-slate-700 mb-2">Block Type</label>
            <select
              value={newBlockType}
              onChange={(e) => setNewBlockType(e.target.value)}
              className="w-full border border-slate-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent bg-white shadow-sm"
            >
              <option value="hero_banner">Hero Banner</option>
              <option value="trending_content">Trending Content</option>
              <option value="category_carousel">Category Carousel</option>
              <option value="premium_collection">Premium Collection</option>
              <option value="heritage_spotlight">Heritage Spotlight</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">Initial Config (JSON)</label>
            <textarea
              value={newBlockConfig}
              onChange={(e) => setNewBlockConfig(e.target.value)}
              className="w-full h-24 p-3 text-sm font-mono border border-slate-300 rounded-md focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none shadow-sm"
            />
          </div>
        </div>
        <button
          onClick={addBlock}
          className="flex items-center justify-center gap-2 w-full md:w-auto bg-slate-100 text-slate-700 hover:bg-slate-200 px-6 py-2 rounded-md transition-colors font-medium"
        >
          Add Block to Layout
        </button>
      </div>
    </div>
  );
}
